import * as React from 'react';

import { cva } from 'class-variance-authority';
import {
  Dialog as AriaDialog,
  DialogProps as AriaDialogProps,
  DialogTrigger as AriaDialogTrigger,
  Popover as AriaPopover,
  PopoverProps as AriaPopoverProps,
  composeRenderProps,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';

const PopoverTrigger = AriaDialogTrigger;

// Define popover variants
const popoverVariants = cva([
  'z-50 transform rounded-md border border-neutral/40 bg-white',
  'text-neutral/90 shadow-md outline-none',
]);

// Define popover dialog variants
const popoverDialogVariants = cva('outline-0');

/**
 * Popover component for displaying floating content.
 * Creates a popover container that appears relative to its trigger element.
 *
 * @example
 * ```tsx
 * <PopoverTrigger>
 *   <Button>Open Popover</Button>
 *   <Popover>
 *     <PopoverDialog>Popover content</PopoverDialog>
 *   </Popover>
 * </PopoverTrigger>
 * ```
 *
 * @param props - Popover properties
 * @param props.offset - Distance in pixels between the popover and its trigger element (default: 4)
 */
const Popover = ({
  className,
  offset = 4,
  ...props
}: AriaPopoverProps & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <AriaPopover
    offset={offset}
    className={composeRenderProps(className, (className) =>
      cn('@Popover', popoverVariants(), className),
    )}
    {...props}
  />
);

/**
 * Dialog component for popovers.
 * Renders the content inside a popover with appropriate styling and accessibility attributes.
 *
 * @param props - PopoverDialog properties
 */
const PopoverDialog = ({ className, ...props }: AriaDialogProps) => (
  <AriaDialog
    className={cn('@PopoverDialog', popoverDialogVariants(), className)}
    {...props}
  />
);

export { Popover, PopoverTrigger, PopoverDialog };
